// İSG uzmanı portalı — Şube bilgileri (api-contract §10.2, tenant-scoped). Firmanın
// şube listesi (UTable sözleşmesi: page/pageSize/search + total/filteredTotal),
// oluştur/düzenle (BranchFormModal), sil (soft-delete, onay). Firmaya kapsanmış —
// başka firma şubesi görünmez (backend ScopedQuery). §9 admin şube ekranından ayrıdır.

import {
  Alert,
  type BranchSettingsDto,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  Pagination,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  resolveApiErrorMessage,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building, Plus, SearchX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { BranchFormModal } from '../../components/settings/BranchFormModal';
import { settingsKeys } from '../../lib/settingsKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

export function SettingsBranchesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editBranch, setEditBranch] = useState<BranchSettingsDto | null>(null);
  const [deleteBranch, setDeleteBranch] = useState<BranchSettingsDto | null>(null);

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    sortBy: 'name',
    sortDir: 'asc' as const,
  };

  const query = useQuery({
    queryKey: settingsKeys.branches(params),
    queryFn: () => settingsApi.listBranches(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => settingsApi.deleteBranch(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.branches() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.branchOptions() });
      setMessage(t('settings.branches.deleted'));
      setDeleteBranch(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch);
  function clearFilters() {
    setSearch('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('settings.branches.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('settings.branches.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('settings.common.search')}
            searchPlaceholder={t('settings.branches.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
          />

          <Table caption={t('settings.branches.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settings.branches.col.name')}</TableHead>
                <TableHead>{t('settings.branches.col.code')}</TableHead>
                <TableHead>{t('settings.branches.col.address')}</TableHead>
                <TableHead>{t('settings.branches.col.phone')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('settings.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={5} />
              ) : query.isError ? (
                <TableMessageRow colSpan={5}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={5}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('settings.common.empty')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={Building}
                      title={t('settings.common.noData')}
                      description={t('settings.common.noData.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('settings.branches.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((branch) => (
                  <TableRow key={branch.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — ayrı bir
                          görünüm rotası yok, "açmak" düzenleme modalını açmakla AYNI. */}
                      <button
                        type="button"
                        onClick={() => {
                          setMessage(null);
                          setEditBranch(branch);
                        }}
                        className="text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {branch.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-muted">
                      {branch.code ? (
                        <code className="rounded bg-surface-muted px-1.5 py-0.5 text-xs">
                          {branch.code}
                        </code>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-muted">{branch.address ?? '—'}</TableCell>
                    <TableCell className="text-muted">{branch.phone ?? '—'}</TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('settings.branches.action.openMenu', { name: branch.name })}
                        items={[
                          {
                            key: 'delete',
                            label: t('settings.branches.action.delete'),
                            destructive: true,
                            onSelect: () => {
                              setMessage(null);
                              setDeleteBranch(branch);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('settings.pagination.nav'),
                previous: t('settings.pagination.previous'),
                next: t('settings.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <BranchFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setMessage(t('settings.branches.created'))}
      />
      <BranchFormModal
        open={editBranch !== null}
        onOpenChange={(open) => !open && setEditBranch(null)}
        branch={editBranch}
        onSuccess={() => setMessage(t('settings.branches.updated'))}
      />
      <ConfirmDialog
        open={deleteBranch !== null}
        onOpenChange={(open) => !open && setDeleteBranch(null)}
        title={t('settings.branches.delete.title')}
        body={t('settings.branches.delete.body', { name: deleteBranch?.name ?? '' })}
        confirmLabel={t('settings.branches.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteBranch && deleteMutation.mutate(deleteBranch.uuid)}
      />
    </div>
  );
}
